import Vue from 'vue'
import Vuex from 'vuex'
import { db } from '../firebase.js'
import { v1 as uuidv1 } from 'uuid'

Vue.use(Vuex)

// Small helper function to shuffle an Array
const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]]
  }
  return array
}

export default function makeStore () {
  return new Vuex.Store({
    state: {
      questionnaireId: false,
      ownerId: false,
      settings: {},
      route: [],
      products: [],
      questions: [],
      answers: [],
      weightedProducts: [],
      step: 0,
      reportSent: false
    },
    mutations: {
      UPDATE_QUESTIONNAIRE_ID (state, payload) {
        state.questionnaireId = payload
      },
      UPDATE_OWNER_ID (state, payload) {
        state.ownerId = payload
      },
      UPDATE_SETTINGS (state, payload) {
        state.settings = payload
      },
      UPDATE_PRODUCTS (state, payload) {
        state.products = payload
      },
      UPDATE_QUESTIONS (state, payload) {
        state.questions = payload
      },
      UPDATE_ANSWERS (state, payload) {
        state.answers = payload
      },
      UPDATE_STEP (state, payload) {
        state.step = payload
      },
      UPDATE_ROUTE (state, payload) {
        state.route = payload
      },
      PUSH_ROUTE (state, payload) {
        state.route.push(payload)
      },
      POP_ROUTE (state) {
        state.route.pop()
      },
      UPDATE_REPORT_SENT (state, payload) {
        state.reportSent = payload
      }
    },
    actions: {
      updateStep ({ commit, state }, { step, pop = false }) {
        if (!pop && step < state.questions.length) {
          commit('PUSH_ROUTE', step)
        } else {
          commit('POP_ROUTE')
        }
        commit('UPDATE_STEP', step)
      },
      updateQuestionnaireId ({ commit }, questionnaireId) {
        commit('UPDATE_QUESTIONNAIRE_ID', questionnaireId)
      },
      updateOwnerId ({ commit }, ownerId) {
        commit('UPDATE_OWNER_ID', ownerId)
      },
      updateSettings ({ commit }, settings) {
        commit('UPDATE_SETTINGS', settings)
      },
      updateProducts ({ commit }, products) {
        commit('UPDATE_PRODUCTS', products)
      },
      updateQuestions ({ commit }, questions) {
        return new Promise((resolve, reject) => {
          if (questions.length > 0) {
            questions.forEach(question => {
              question.answers.forEach(choice => {
                choice._id = uuidv1()
              })
            })
            commit('UPDATE_QUESTIONS', questions)
            resolve()
          } else {
            reject(new Error('Empty products object.'))
          }
        })
      },
      popAnswer ({ commit, state }) {
        const tempAnswers = [...state.answers]
        tempAnswers.pop()
        commit('UPDATE_ANSWERS', tempAnswers)
      },
      pushAnswer ({ dispatch, commit, state }, answer) {
        const tempAnswers = [...state.answers]
        tempAnswers.push(answer)
        commit('UPDATE_ANSWERS', tempAnswers)

        if (answer.answer.stopEnabled) {
          dispatch('updateStep', { step: state.questions.length })
        }

        if (answer.answer.jumpEnabled) {
          const targetIndex = state.questions.findIndex(question => question.id === answer.answer.jumpTarget)
          dispatch('updateStep', { step: targetIndex })
        } else {
          dispatch('updateStep', { step: state.step + 1 })
        }
      },
      getPreviousQuestion ({ dispatch, commit, state }) {
        const previousStep = state.route[state.route.length - 2]
        dispatch('popAnswer')
        dispatch('updateStep', { step: previousStep, pop: true })
      },
      resetQuestionnaire ({ dispatch, commit }) {
        commit('UPDATE_ROUTE', [])
        commit('UPDATE_ANSWERS', [])
        dispatch('updateStep', { step: 0 })
      },
      sendReport ({ dispatch, commit, getters, state }) {
        if (!state.reportSent) {
          const report = {
            questionnaireId: state.questionnaireId,
            answers: state.answers,
            results: getters.results,
            createdOn: new Date()
          }
          try {
            db.collection('reports')
              .doc(state.questionnaireId)
              .collection('reports')
              .add(report)
            commit('UPDATE_REPORT_SENT', true)
          } catch (error) {
            console.error('Error creating the report. ', error)
          }
        }
      }
    },
    getters: {
      points: state => {
        const points = {}
        state.answers.forEach(answer => {
          answer.answer.rules.forEach(rule => {
            // If product already has points
            if (points[rule.product]) {
              points[rule.product].points += parseInt(rule.points)
            } else { // Else we add the product to the points object
              const theProduct = state.products.find(product => product.id === rule.product)
              if (theProduct) {
                points[rule.product] = Object.assign(theProduct, {
                  points: parseInt(rule.points)
                })
              }
            }
          })
        })
        return Object.values(points)
      },
      results: (state, getters) => {
        const clonedPoints = getters.points.slice()
        const sortedResults = clonedPoints.sort((a, b) => (b.points > a.points) ? 1 : -1)

        // Create an array of array, one array per "points value"
        const resultsPointsArray = []
        let arrayCount = 0
        let currentArrayPoints = false
        sortedResults.forEach(result => {
          if (currentArrayPoints === false) {
            currentArrayPoints = result.points
            resultsPointsArray.push([result])
          } else {
            if (currentArrayPoints === result.points) {
              resultsPointsArray[arrayCount].push(result)
            } else {
              arrayCount++
              currentArrayPoints = result.points
              resultsPointsArray.push([result])
            }
          }
        })

        // Shuffle all those sub-arrays
        const shuffledResultsPointsArray = []
        resultsPointsArray.forEach(resultsArray => {
          shuffledResultsPointsArray.push(shuffleArray(resultsArray))
        })

        // Concat all the sub-arrays into one array of shuffled-but-sorted results
        let shuffledSortedResults = []
        shuffledResultsPointsArray.forEach(shuffledResultsArray => {
          shuffledSortedResults = shuffledSortedResults.concat(shuffledResultsArray)
        })

        return shuffledSortedResults.slice(0, state.settings.numberOfResults)
      }
    },
    modules: {
    }
  })
}
